import { useEffect } from 'react'

import i18n from 'modules/core/lib/i18n'
import {
  NodeUI,
  NodeUIApproval,
  NodeUITask,
  isNodeUIMultiAction,
  isNodeUITask,
} from 'modules/workflows/components/Node/types'
import ConditionalNodeSidepanel from 'modules/workflows/components/Sidepanel/ConditionalNodeSidepanel/index'
import TaskNodeSidepanel from 'modules/workflows/components/Sidepanel/TaskNodeSidepanel'
import TriggerNodeSidepanel from 'modules/workflows/components/Sidepanel/TriggerNodeSidepanel'
import { VariablesConfig } from 'modules/workflows/components/Sidepanel/VariableSelectorTypes'
import {
  BuildingBlock,
  WorkflowBuildingBlocksBox,
} from 'modules/workflows/components/WorkflowBuildingBlocksBox'
import { useWorkflowContext } from 'modules/workflows/contexts/WorkflowContext'

import {
  ApprovalNodeSidepanel,
  getDefaultManualReviewHandlerName,
} from './ApprovalNodeSidepanel'
import { MultiActionNodeSidepanel } from './MultiActionNodeSidepanel'
import { SidePanelFormProvider } from './SidePanelFormProvider'

type Props = {
  showBuildingBlock: boolean
  selectedNode: NodeUI | undefined
  buildingBlocks: BuildingBlock[]
  variablesConfig: VariablesConfig
  conditionsHeaderLabel?: string
  dynamicInputsHeaderLabel?: string
}

/** Builds a Task/Approval node shape from a parallel task so we can reuse TaskNodeSidepanel/ApprovalNodeSidepanel for multi-action items (draft or edit). */
function buildVirtualActionNode(
  task: { id: string; type: 'task' | 'approvals'; title: string; description: string; handler_name: string; payload: Record<string, unknown> }
): NodeUITask | NodeUIApproval {
  const base = {
    id: task.id,
    data: {
      title: task.title,
      description: task.description,
      handler_name: task.handler_name,
      payload: task.payload,
      isValid: true,
    },
    position: { x: 0, y: 0 },
    selected: false,
  }
  return task.type === 'task'
    ? { ...base, type: 'task' } satisfies NodeUITask
    : { ...base, type: 'approvals' } satisfies NodeUIApproval
}

export const Sidepanel = ({
  showBuildingBlock,
  selectedNode,
  buildingBlocks,
  variablesConfig,
  conditionsHeaderLabel,
  dynamicInputsHeaderLabel,
}: Props) => {
  const {
    multiActionEditingTaskId,
    setMultiActionEditingTaskId,
    multiActionDraft,
    setMultiActionDraft,
    setNodesUI,
    workflowManifest,
    handlersConfig,
  } = useWorkflowContext()

  // Clear editing task id when the task is no longer in the list (e.g. removed)
  useEffect(() => {
    if (!isNodeUIMultiAction(selectedNode) || !multiActionEditingTaskId) return
    const task = selectedNode.data.parallelTasks?.find(
      (t) => t.id === multiActionEditingTaskId
    )
    if (!task) setMultiActionEditingTaskId(null)
  }, [selectedNode, multiActionEditingTaskId, setMultiActionEditingTaskId])

  // Multi Action: decide which view to show (draft form, edit form, or list).
  const multiNode = selectedNode && isNodeUIMultiAction(selectedNode) ? selectedNode : null
  const multiActionViewMode: 'draft' | 'edit' | 'list' | null = multiNode
    ? multiActionDraft?.nodeId === multiNode.id
      ? 'draft'
      : multiActionEditingTaskId
        ? 'edit'
        : 'list'
    : null

  // Build config for the single draft/edit form (virtual node + callbacks). Used when viewMode is 'draft' or 'edit'.
  const multiActionFormConfig =
    multiNode && (multiActionViewMode === 'draft' || multiActionViewMode === 'edit')
      ? (() => {
          if (multiActionViewMode === 'draft' && multiActionDraft?.nodeId === multiNode.id) {
            const draftType = multiActionDraft.type
            const defaultTitle =
              draftType === 'task'
                ? i18n.t('workflows.node_type.task.default.title')
                : i18n.t('workflows.node_type.approval.default.title')
            const defaultDescription =
              draftType === 'task'
                ? i18n.t('workflows.node_type.task.default.description')
                : i18n.t('workflows.node_type.approval.default.description')
            const defaultApprovalHandler =
              draftType === 'approvals'
                ? getDefaultManualReviewHandlerName(
                    workflowManifest.handlers ?? [],
                    handlersConfig
                  )
                : undefined
            const virtualNode = buildVirtualActionNode({
              id: 'draft',
              type: draftType,
              title: defaultTitle,
              description: defaultDescription,
              handler_name: defaultApprovalHandler ?? '',
              payload: {},
            })
            return { mode: 'draft' as const, virtualNode, multiNode, editingTaskId: null }
          }
          const task = multiNode.data.parallelTasks?.find((t) => t.id === multiActionEditingTaskId)
          if (!task) return null
          const virtualNode = buildVirtualActionNode(task)
          return {
            mode: 'edit' as const,
            virtualNode,
            multiNode,
            editingTaskId: task.id,
          }
        })()
      : null

  return (
    <>
      {showBuildingBlock && <WorkflowBuildingBlocksBox buildingBlocks={buildingBlocks} />}

      {selectedNode?.type === 'multiAction' && multiNode && (
        <>
          {multiActionFormConfig ? (
            (() => {
              const config = multiActionFormConfig
              const onClose = () =>
                config.mode === 'draft' ? setMultiActionDraft(null) : setMultiActionEditingTaskId(null)
              const onSave = (data: {
                title: string
                description: string
                handler_name: string
                payload: Record<string, unknown>
              }) => {
                if (config.mode === 'draft') {
                  const newTask = {
                    id: crypto.randomUUID(),
                    type: config.virtualNode.type,
                    title: data.title,
                    description: data.description,
                    handler_name: data.handler_name,
                    payload: data.payload,
                    action_group: config.virtualNode.type,
                  }
                  setNodesUI((prev) =>
                    prev.map((n) => {
                      if (n.id !== multiNode.id || n.type !== 'multiAction') return n
                      const currentTasks = n.data.parallelTasks ?? []
                      return {
                        ...n,
                        data: { ...n.data, parallelTasks: [...currentTasks, newTask] },
                      }
                    })
                  )
                  setMultiActionDraft(null)
                } else {
                  setNodesUI((prev) =>
                    prev.map((n) => {
                      if (n.id !== multiNode.id || n.type !== 'multiAction') return n
                      return {
                        ...n,
                        data: {
                          ...n.data,
                          parallelTasks: n.data.parallelTasks.map((t) =>
                            t.id === config.editingTaskId ? { ...t, ...data } : t
                          ),
                        },
                      }
                    })
                  )
                  setMultiActionEditingTaskId(null)
                }
              }
              return (
                <SidePanelFormProvider
                  variablesConfig={variablesConfig}
                  selectedNode={config.virtualNode}
                  key={`multiAction-${config.mode}-${multiNode.id}-${config.virtualNode.id}`}
                >
                  {isNodeUITask(config.virtualNode) ? (
                    <TaskNodeSidepanel
                      node={config.virtualNode}
                      onBack={onClose}
                      onCancel={onClose}
                      onSave={onSave}
                    />
                  ) : (
                    <ApprovalNodeSidepanel
                      node={config.virtualNode}
                      onBack={onClose}
                      onCancel={onClose}
                      restrictToManualReviewOnly
                      onSave={onSave}
                    />
                  )}
                </SidePanelFormProvider>
              )
            })()
          ) : (
            /* List view, or fallback when edit target task was removed */
            <MultiActionNodeSidepanel node={multiNode} />
          )}
        </>
      )}

      {selectedNode && selectedNode.type !== 'multiAction' && (
        <SidePanelFormProvider
          variablesConfig={variablesConfig}
          selectedNode={selectedNode}
          key={selectedNode.id}
        >
          {selectedNode.type === 'trigger' && (
            <TriggerNodeSidepanel key={selectedNode.id} node={selectedNode} />
          )}
          {selectedNode.type === 'condition' && (
            <ConditionalNodeSidepanel
              key={selectedNode.id}
              node={selectedNode}
              variablesConfig={variablesConfig}
              conditionsHeaderLabel={conditionsHeaderLabel}
              dynamicInputsHeaderLabel={dynamicInputsHeaderLabel}
            />
          )}
          {selectedNode.type === 'approvals' && (
            <ApprovalNodeSidepanel key={selectedNode.id} node={selectedNode} />
          )}
          {selectedNode.type === 'task' && (
            <TaskNodeSidepanel key={selectedNode.id} node={selectedNode} />
          )}
        </SidePanelFormProvider>
      )}
    </>
  )
}
